import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ObserveProperty } from "@calaosoft/osapp-common/observable/decorators/observe-property.decorator";
import { ObservableProperty } from "@calaosoft/osapp-common/observable/models/observable-property";
import { NumberHelper } from "@calaosoft/osapp-common/utils/helpers/numberHelper";
import { StringHelper } from "@calaosoft/osapp-common/utils/helpers/stringHelper";
import { DestroyableComponentBase } from "../../../../utils/components/destroyable-component-base";
import { ICategoryTileParams } from "../models/icategory-tile-params";

interface HTMLCategoryTile extends HTMLElement {}

@Component({
	selector: "calao-category-tile",
	templateUrl: "./category-tile.component.html",
	styleUrls: ["./category-tile.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CategoryTileComponent extends DestroyableComponentBase implements ICategoryTileParams, OnInit {
	//#region FIELDS

	/** Nom de la variable CSS à modifier pour changer la couleur du titre de la tuile. */
	private static readonly C_TILE_TITLE_COLOR_VAR_NAME = "--tile-title-color";
	/** Nom de la variable CSS à modifier pour changer la couleur de l'arrière-plan du titre de la tuile. */
	private static readonly C_TILE_TITLE_BACKGROUND_COLOR_VAR_NAME = "--tile-title-background-color";
	/** Nom de la variable CSS à modifier pour changer la couleur de la valeur de la tuile. */
	private static readonly C_TILE_VALUE_COLOR_VAR_NAME = "--tile-value-color";
	/** Nom de la variable CSS à modifier pour changer la couleur de l'arrière-plan de la valeur de la tuile. */
	private static readonly C_TILE_VALUE_BACKGROUND_COLOR_VAR_NAME = "--tile-value-background-color";
	/** Nom de la variable CSS à modifier pour changer la couleur de bordure de la tuile. */
	private static readonly C_TILE_BORDER_COLOR_VAR_NAME = "--tile-border-color";

	@Output("click") private readonly moClickEvent = new EventEmitter<void>();

	//#endregion

	//#region PROPERTIES

	/** @implements */
	@Input({ required: true }) public title?: string;
	@ObserveProperty<CategoryTileComponent>({ sourcePropertyKey: "title" })
	public readonly obsTitle = new ObservableProperty<string>("");

	/** @implements */
	@Input() public titleColor?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "titleColor",
		setAction: (poThis: CategoryTileComponent) =>
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_TITLE_COLOR_VAR_NAME, poThis.obsTitleColor.value)
	})
	public readonly obsTitleColor = new ObservableProperty<string>();

	/** @implements */
	@Input() public titleBackgroundColor?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "titleBackgroundColor",
		setAction: (poThis: CategoryTileComponent) =>
			poThis.setCssVarProperty(
				CategoryTileComponent.C_TILE_TITLE_BACKGROUND_COLOR_VAR_NAME,
				poThis.obsTitleBackgroundColor.value
			)
	})
	public readonly obsTitleBackgroundColor = new ObservableProperty<string>("white");

	/** @implements */
	@Input() public value?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "value",
		transformer: (psValue?: string) => (psValue && NumberHelper.isValidPositive(+psValue) ? psValue : "-")
	})
	public readonly obsValue = new ObservableProperty<string>();

	/** @implements */
	@Input() public valueColor?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "valueColor",
		setAction: (poThis: CategoryTileComponent) =>
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_VALUE_COLOR_VAR_NAME, poThis.obsValueColor.value)
	})
	public readonly obsValueColor = new ObservableProperty<string>("black");

	/** @implements */
	@Input() public valueBackgroundColor?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "valueBackgroundColor",
		setAction: (poThis: CategoryTileComponent) =>
			poThis.setCssVarProperty(
				CategoryTileComponent.C_TILE_VALUE_BACKGROUND_COLOR_VAR_NAME,
				poThis.obsValueBackgroundColor.value
			)
	})
	public readonly obsValueBackgroundColor = new ObservableProperty<string>("white");

	/** @implements */
	@Input() public icon?: string;
	@ObserveProperty<CategoryTileComponent>({ sourcePropertyKey: "icon" })
	public readonly obsIcon = new ObservableProperty<string>();

	/** @implements */
	@Input() public hasBorder?: boolean;
	@ObserveProperty<CategoryTileComponent>({ sourcePropertyKey: "hasBorder" })
	public readonly obsHasBorder = new ObservableProperty<boolean>(false);

	/** @implements */
	@Input() public borderColor?: string;
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "borderColor",
		setAction: (poThis: CategoryTileComponent) =>
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_BORDER_COLOR_VAR_NAME, poThis.obsBorderColor.value)
	})
	public readonly obsBorderColor = new ObservableProperty<string>();

	//todo : supprimer les champs ci-dessous une fois la migration des merchs effectuée.
	private static readonly C_TILE_SUBTITLE_BACKGROUND_COLOR_VAR_NAME = "--tile-subtitle-background-color";
	/** Indique si on est en mode de rétrocompatibilité et doit afficher l'ancien affichage.
	 * @deprecated
	 */
	@Input() public isOldDisplaying?: boolean;
	/** @deprecated */
	@ObserveProperty<CategoryTileComponent>({ sourcePropertyKey: "isOldDisplaying" })
	public readonly obsIsOldDisplaying = new ObservableProperty<boolean>();
	/** Sous-titre de la tuile dans le cas de la rétrocompatibilité uniquement.
	 * @deprecated
	 */
	@Input() public subtitle?: string;
	/** @deprecated */
	@ObserveProperty<CategoryTileComponent>({
		sourcePropertyKey: "subtitle",
		setAction: (poThis: CategoryTileComponent) => {
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_SUBTITLE_BACKGROUND_COLOR_VAR_NAME, poThis.titleColor);
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_TITLE_COLOR_VAR_NAME, "black");
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_TITLE_BACKGROUND_COLOR_VAR_NAME, "white");
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_VALUE_COLOR_VAR_NAME, "#993366");
			poThis.setCssVarProperty(CategoryTileComponent.C_TILE_VALUE_BACKGROUND_COLOR_VAR_NAME, "white");
		}
	})
	public readonly obsSubtitle = new ObservableProperty<string>();

	//#endregion

	//#region METHODS

	public constructor(private readonly ioElementRef: ElementRef<HTMLCategoryTile>) {
		super();
	}

	public ngOnInit(): void {
		if (StringHelper.isBlank(this.titleColor)) this.titleColor = "black";

		if (StringHelper.isBlank(this.titleBackgroundColor)) this.titleBackgroundColor = "white";

		if (StringHelper.isBlank(this.valueColor)) this.valueColor = "black";

		if (StringHelper.isBlank(this.valueBackgroundColor)) this.valueBackgroundColor = "white";
	}

	private setCssVarProperty(psVarName: string, psColor?: string): void {
		if (this.ioElementRef && StringHelper.isValid(psColor))
			this.ioElementRef.nativeElement.style.setProperty(psVarName, psColor);
	}

	public onClick(): void {
		this.moClickEvent.emit();
	}

	//#endregion
}
